import React, { useEffect, useState } from 'react'
import Wrapper from '../../Wrapper'
import { Linking, StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import { formatIncompletePhoneNumber } from 'libphonenumber-js'
import { Link } from '../../../../ui/atoms/Link'
import { useStore } from '../../../../store'
import { toJS } from 'mobx'
import { editableModel, routes } from '@linqpal/models'
import { types } from 'mobx-state-tree'
import { HEADER_HEIGHT } from '..'
import { useNavigation } from '@react-navigation/core'
import { BackHeader } from '../../../../assets/icons'
import { GetIcon } from '../../../../ui/atoms/GetIcon'
import { InputField } from '../../../../ui/atoms/builder-2.0/InputField'
import { Input } from '../../../../ui/atoms/builder-2.0/Input'
import { useResponsive } from '../../../../utils/hooks'
import { useTranslation } from 'react-i18next'
import {
  validateEmail,
  validateUSPhoneNumber,
} from '@linqpal/models/src/helpers/validations'
import { ActivityIndicator } from 'react-native-paper'
import { observer } from 'mobx-react'
import { fileDownloader } from '@linqpal/common-frontend/src/helpers'
import { isWeb } from '../../../../utils/helpers/commonUtils'
import { commonColors } from '@linqpal/common-frontend/src/theme'

const backBehavior = (navigation) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  navigation.canGoBack() ? navigation.goBack() : navigation.navigate('More')
}

const UserModel = types
  .compose(
    types.model({
      sub: types.maybeNull(types.string),
      name: types.maybeNull(types.string),
      login: types.maybeNull(types.string),
      firstName: types.maybeNull(types.string),
      lastName: types.maybeNull(types.string),
      companyName: types.maybeNull(types.string),
      email: types.maybeNull(types.string),
      phone: types.maybeNull(types.string),
    }),
    editableModel(),
  )
  .views((self) => ({
    get isDisabled() {
      return (
        validateEmail(self.email || undefined) &&
        validateUSPhoneNumber(self.phone || undefined)
      )
    },
  }))

const AccountHeader = () => {
  const { t } = useTranslation('global')
  const navigation = useNavigation()
  return (
    <View
      style={{
        height: HEADER_HEIGHT,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#F5F7F8',
        width: 400,
      }}
    >
      <GetIcon
        icon={<BackHeader height="16" width="16" />}
        onPress={() => backBehavior(navigation)}
      />
      <View style={{ flex: 1, alignItems: 'center' }}>
        <Text style={{ fontWeight: '500', fontSize: 16 }}>
          {t('Profile.profile')}
        </Text>
      </View>
    </View>
  )
}

export const Account = observer(() => {
  const { t } = useTranslation('global')
  const { userStore } = useStore()
  const { user, company, credit } = userStore
  const { screenHeight } = useResponsive()

  const [updateUser, setUser] = useState<any>(null)
  const [hasGuarantorAgreement, setHasGuarantorAgreement] = useState(false)

  useEffect(() => {
    Promise.all([
      routes.contractor.hasPersonalGuarantorAgreement(),
      // since app submission is background process,
      // LoC download link won't appear right after submission, force refresh it
      !credit?.LoCnumber ? userStore.fetchUser() : Promise.resolve(),
    ])
      .then(([guarantorAgreement, _]) =>
        setHasGuarantorAgreement(guarantorAgreement.hasAgreement),
      )
      .catch((e) => console.log(e))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const userProfile = toJS(user)
    const userProfilePhone =
      userProfile?.phone?.replace(/^\+[0-9]/, '').trim() || ''
    const formattedUserProfilePhone = formatIncompletePhoneNumber(
      userProfilePhone,
      'US',
    )

    const u = {
      ...userProfile,
      phone: formattedUserProfilePhone,
      companyName: company?.legalName || company?.name,
    }
    setUser(UserModel.create(toJS(u)))
  }, [company?.legalName, company?.name, user])

  const onDownloadMasterAgreement = () =>
    routes.contractor.tryGetMasterAgreement().then((res) => {
      if (isWeb) {
        window.open(res.url, '_blank')
      } else {
        Linking.openURL(res.url)
      }
    })

  const onDownloadGuarantorAgreement = () =>
    routes.contractor
      .downloadPersonalGuarantorAgreements()
      .then((response) =>
        fileDownloader(response.url, {}, response.fileName, 'application/zip'),
      )

  return (
    <Wrapper toolbar={<AccountHeader />}>
      <View
        style={{
          backgroundColor: 'white',
          flexDirection: 'column',
          justifyContent: 'space-between',
          flex: 1,
        }}
      >
        <View style={{ backgroundColor: 'white', height: screenHeight - 200 }}>
          <Input
            value={
              (updateUser?.firstName || '') + ' ' + (updateUser?.lastName || '')
            }
            label={t('Account.full_name')}
            onChangeText={() => {}}
            disabled
          />
          <InputField
            model={updateUser}
            editable={false}
            label={t('Account.business-name')}
            name="companyName"
          />
          <InputField
            model={updateUser}
            editable={false}
            label={t('Account.email')}
            name="email"
            mandatory
            validateInput={(value) => {
              return !validateEmail(value) ? 'Invalid email' : ''
            }}
          />
          <InputField
            model={updateUser}
            editable={false}
            label={t('Account.cell-phone-number')}
            name="phone"
            mandatory
            validateInput={(value) => {
              return !validateUSPhoneNumber(value) ? 'Invalid phone number' : ''
            }}
          />
          {!!credit?.LoCnumber && (
            <AgreementLink
              downloadFile={onDownloadMasterAgreement}
              title={t('Account.click-here-to-download-LoC')}
            />
          )}
          {hasGuarantorAgreement && (
            <AgreementLink
              downloadFile={onDownloadGuarantorAgreement}
              title={t('Account.click-here-to-download-PGA')}
            />
          )}
        </View>
        <View style={{ paddingVertical: 25 }}>
          <Link
            title={t('Account.to_deactivate_account')}
            color="#99ADBA"
            textStyle={{ fontSize: 15, textAlign: 'center' }}
            onPress={() => null}
          />
        </View>
      </View>
    </Wrapper>
  )
})

const AgreementLink = ({
  downloadFile,
  title,
}: {
  downloadFile: () => Promise<void>
  title: string
}): JSX.Element => {
  const [loading, setLoading] = useState(false)

  const onClick = () => {
    setLoading(true)
    downloadFile().finally(() => setLoading(false))
  }

  return (
    <>
      {loading ? (
        <ActivityIndicator
          style={styles.agreementLoadingIndicator}
          color={commonColors.accentText}
        />
      ) : (
        <TouchableOpacity onPress={onClick}>
          <Text style={styles.agreementLink}>{title}</Text>
        </TouchableOpacity>
      )}
    </>
  )
}

const styles = StyleSheet.create({
  agreementLink: {
    fontWeight: '600',
    fontSize: 16,
    lineHeight: 24,
    color: '#00A0F3',
    textAlign: 'center',
    marginTop: 40,
    width: 250,
    alignSelf: 'center',
  },
  agreementLoadingIndicator: {
    color: '#00A0F3',
    marginLeft: 10,
    marginTop: 40,
  },
})
